import React from 'react'
import ReactDOM from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ConfigProvider } from 'antd'
import App from './App'
import PublicHydrationRoot from './ssr/PublicHydrationRoot'
import './index.css'

declare global {
  interface Window {
    __SSR_ROUTE__?: string
    __SSR_PATHNAME__?: string
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
})

const theme = {
  token: {
    colorPrimary: '#ea4335',
    borderRadius: 8,
    fontSize: 14,
  },
}

const rootEl = document.getElementById('root')!
const ssrPath = typeof window !== 'undefined' ? (window.__SSR_ROUTE__ ?? window.__SSR_PATHNAME__) : undefined
const currentPath = typeof window !== 'undefined' ? window.location.pathname || '/' : '/'
const pathMatch = ssrPath !== undefined && (currentPath === ssrPath || (ssrPath === '/' && (currentPath === '' || currentPath === '/')))
const hasSSRContent = rootEl?.innerHTML?.trim().length > 0
// SSO from ServiceFlow opens /?token=… — must mount App (useAuth), not the public SSR landing shell.
const hasSsoToken =
  typeof window !== 'undefined' &&
  Boolean(new URLSearchParams(window.location.search).get('token'))
const shouldHydrate = pathMatch && hasSSRContent && !hasSsoToken

if (shouldHydrate) {
  ReactDOM.hydrateRoot(rootEl, <PublicHydrationRoot />)
} else {
  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ConfigProvider theme={theme}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </ConfigProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </React.StrictMode>
  )
}
